import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { Notification } from '../../models/notifications/notification.interface';
import { NotificationResult } from '../../models/notifications/notification-result.interface';
import { AnnouncementStatus } from '../../models/notifications/announcement-status.interface';
import { NotificationAccess } from '../../models/notifications/notification-access.interface';
import { catchError } from 'rxjs/operators';
import { withCache } from '@ngneat/cashew';
import { constants } from '@edassist/common';

@Injectable({ providedIn: 'root' })
export class NotificationService {
	constants = constants;
	private readonly relativeUrl = 'participants/';
	constructor(private readonly http: HttpClient) {}

	getParticipantUnreadNotificationEventCount(
		participantId: number
	): Observable<number> {
		return this.http
			.get<number>(
				`${this.relativeUrl}${participantId.toString()}/notifications/unread-count`
			)
			.pipe(
				catchError(() => {
					const notificationCount: number = undefined;

					return of(notificationCount);
				})
			);
	}

	getParticipantNotificationEvents(
		participantId: number,
		pageNumber: number,
		pageSize: number,
		continuationToken: string
	): Observable<NotificationResult> {
		return this.http
			.get<NotificationResult>(`${this.relativeUrl}${participantId.toString()}/notifications
		?pageNumber=${pageNumber}&pageSize=${pageSize}&continuationToken=${continuationToken}`);
	}

	updateNotification(notification: Notification): Observable<Notification> {
		return this.http.put<Notification>(
			`${this.relativeUrl}${notification.participantId}/notifications/${notification.id}`,
			notification
		);
	}

	updateNotificationsAsRead(
		participantId: number,
		notificationIds: Array<string>
	): Observable<Array<Notification>> {
		return this.http.put<Array<Notification>>(
			`${this.relativeUrl}${participantId.toString()}/notifications`,
			notificationIds
		);
	}

	getAnnouncementsStatuses(
		participantId: number
	): Observable<Array<AnnouncementStatus>> {
		return this.http.get<Array<AnnouncementStatus>>(
			`${this.relativeUrl}${participantId.toString()}/announcement-notifications/statuses`
		);
	}

	getNotificationAccess(participantId: number): Observable<NotificationAccess> {
		return this.http
			.get<NotificationAccess>(
				`${this.relativeUrl}${participantId.toString()}/notification-access`, {
					context: withCache({
						version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
						key: `notificationAccess-${participantId}`
					})
				}
			)
			.pipe(
				catchError(() => {
					const notificationAccess: NotificationAccess = undefined;

					return of(notificationAccess);
				})
			);
	}

	updateNotificationAccess(
		participantId: number,
		notificationAccess: NotificationAccess
	): Observable<NotificationAccess> {
		return this.http.put<NotificationAccess>(
			`${this.relativeUrl}${participantId.toString()}/notification-access`,
			notificationAccess
		);
	}

	updateAnnouncementsStatuses(
		participantId,
		announcementsStatuses: Array<AnnouncementStatus>
	): Observable<Array<AnnouncementStatus>> {
		return this.http.put<Array<AnnouncementStatus>>(
			`${this.relativeUrl}${participantId.toString()}/announcement-notifications/statuses`,
			announcementsStatuses
		);
	}

	markAllNotificationsAsRead(
		participantId: number,
		notificationFilterType: string
	): Observable<Array<Notification>> {
		return this.http.put<Array<Notification>>(
			`${this.relativeUrl}${participantId.toString()}/notifications/mark-all-read?notificationFilterType=${notificationFilterType}`,
			{}
		);
	}
}
